import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, fmtNum, errMsg } from "@finance/lib/api";
import { useAuth } from "@finance/context/AuthContext";
import Layout, { PageHeader } from "@finance/components/Layout";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@finance/components/ui/tabs";
import { TableEmpty } from "@finance/components/DesignSystem";
import { Paperclip, CheckCircle, ArrowSquareOut, UploadSimple, Truck, Package, Receipt, ClipboardText } from "@phosphor-icons/react";

const Th = ({ children, right }) => <th className={`px-3 py-2 ${right ? "text-right" : "text-left"}`}>{children}</th>;

const ALL_TABS = [
  { key: "pending-signed-delivery-notes", label: "Pending Signed Delivery Notes", icon: Truck, kind: "signed-dn", ep: "/reports/pending-signed-delivery-notes" },
  { key: "pending-signed-grv", label: "Pending Signed GRV", icon: Package, kind: "signed-grv", ep: "/reports/pending-signed-grv" },
  { key: "grni", label: "GRNI — Not Invoiced", icon: ClipboardText, kind: "value", ep: "/reports/grni" },
  { key: "delivered-not-invoiced", label: "Delivered · Not Invoiced", icon: Receipt, kind: "value", ep: "/reports/delivered-not-invoiced" },
];

export default function FulfillmentReports() {
  const { user } = useAuth();
  const TABS = ALL_TABS.filter((t) => user?.role_code !== "documentation" || t.kind === "signed-dn" || t.kind === "signed-grv");
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const initial = TABS.find((t) => t.key === params.get("tab"))?.key || TABS[0].key;
  const [tab, setTab] = useState(initial);
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(false);

  const active = TABS.find((t) => t.key === tab) || TABS[0];

  const load = useCallback(async (t) => {
    const cfg = TABS.find((x) => x.key === t);
    if (!cfg) return;
    setLoading(true);
    try {
      const r = await api.get(cfg.ep);
      setData((d) => ({ ...d, [t]: r.data }));
    } catch (e) { toast.error(errMsg(e)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(tab); }, [tab, load]);

  const onTab = (t) => { setTab(t); setParams({ tab: t }); };

  const attachSigned = async (row, file) => {
    const base = active.kind === "signed-dn" ? "delivery-notes" : "grvs";
    const fd = new FormData(); fd.append("file", file);
    try {
      await api.post(`/${base}/${row.id}/signed-copy`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Signed copy attached to ${row.doc_no}`);
      load(tab);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const cur = data[tab] || {};
  const rows = cur.rows || [];

  return (
    <Layout>
      <PageHeader title="Fulfillment Reports" subtitle="Signed-document tracking and outstanding invoicing worklists" />
      <div className="p-8">
        <Tabs value={tab} onValueChange={onTab}>
          <TabsList data-testid="ful-report-tabs" className="flex-wrap">
            {TABS.map((t) => (
              <TabsTrigger key={t.key} value={t.key} data-testid={`tab-${t.key}`}>
                <t.icon size={15} className="mr-1.5" />{t.label}
              </TabsTrigger>
            ))}
          </TabsList>

          {TABS.map((t) => (
            <TabsContent key={t.key} value={t.key} className="mt-6">
              <div className="mb-3 flex items-center justify-between">
                <div className="text-sm text-gray-500" data-testid="ful-report-count">
                  {loading ? "Loading…" : (
                    <>
                      <span className="font-semibold text-gray-800">{cur.count ?? rows.length}</span> item(s)
                      {cur.total_value != null && <> · Total outstanding <span className="lx-num font-semibold text-[#1B4383]">{fmtNum(cur.total_value)}</span></>}
                    </>
                  )}
                </div>
              </div>

              <div className="overflow-x-auto rounded-sm border border-gray-200 bg-white">
                {(active.kind === "signed-dn" || active.kind === "signed-grv") ? (
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50"><tr className="lx-label border-b border-gray-200">
                      <Th>{active.kind === "signed-grv" ? "GRV No" : "DN No"}</Th><Th>Date</Th><Th>Somcore PO</Th>
                      <Th>{active.kind === "signed-grv" ? "Supplier" : "Client"}</Th><Th right>Qty</Th><Th>By</Th><Th right>Signed Copy</Th>
                    </tr></thead>
                    <tbody>
                      {!loading && rows.length === 0 && <TableEmpty colSpan={7} title="All caught up" message="Every issued document has a signed copy attached." />}
                      {rows.map((r) => (
                        <tr key={r.id} data-testid={`ful-row-${r.id}`} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="px-3 py-2 font-medium">
                            <button className="text-[#1B4383] hover:underline" onClick={() => navigate(`/transactions/${r.transaction_id}`)} data-testid={`open-po-${r.id}`}>{r.doc_no}</button>
                          </td>
                          <td className="px-3 py-2 text-gray-500 lx-num">{r.date || "—"}</td>
                          <td className="px-3 py-2 text-gray-500">{r.lx_po_no || "—"}</td>
                          <td className="px-3 py-2 text-gray-500">{r.party || "—"}</td>
                          <td className="px-3 py-2 text-right lx-num">{fmtNum(r.qty)}</td>
                          <td className="px-3 py-2 text-gray-400 text-xs">{r.created_by || r.received_by || "—"}</td>
                          <td className="px-3 py-2 text-right">
                            <label className="inline-flex cursor-pointer items-center gap-1 rounded-sm bg-[#1B4383] px-2.5 py-1 text-xs text-white hover:bg-[#122E5C]" data-testid={`attach-${r.id}`}>
                              <UploadSimple size={13} /> Attach signed
                              <input type="file" className="hidden" accept="image/*,application/pdf" onChange={(e) => { if (e.target.files?.[0]) attachSigned(r, e.target.files[0]); e.target.value = ""; }} />
                            </label>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50"><tr className="lx-label border-b border-gray-200">
                      <Th>Somcore PO</Th><Th>{active.key === "grni" ? "Supplier" : "Client"}</Th><Th>Date</Th>
                      <Th right>Outstanding Qty</Th><Th right>Outstanding Value</Th><Th />
                    </tr></thead>
                    <tbody>
                      {!loading && rows.length === 0 && <TableEmpty colSpan={6} title="Nothing outstanding" message={active.key === "grni" ? "Every received good has been supplier-invoiced." : "Every delivery has been invoiced to the client."} />}
                      {rows.map((r) => (
                        <tr key={r.transaction_id} data-testid={`ful-row-${r.transaction_id}`} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="px-3 py-2 font-medium">{r.lx_po_no || "—"}</td>
                          <td className="px-3 py-2 text-gray-600">{r.party || "—"}</td>
                          <td className="px-3 py-2 text-gray-500 lx-num">{r.date || "—"}</td>
                          <td className="px-3 py-2 text-right lx-num">{fmtNum(r.outstanding_qty)}</td>
                          <td className="px-3 py-2 text-right lx-num font-semibold">{r.currency} {fmtNum(r.outstanding_value)}</td>
                          <td className="px-3 py-2 text-right">
                            <button className="inline-flex items-center gap-1 text-xs text-[#1B4383] hover:underline" data-testid={`open-po-${r.transaction_id}`} onClick={() => navigate(`/transactions/${r.transaction_id}`)}>Open PO <ArrowSquareOut size={12} /></button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </TabsContent>
          ))}
        </Tabs>
      </div>
    </Layout>
  );
}
